/**
 * One-time, best-effort move of personal saved views from the old lists' localStorage keys
 * into the /requests key. Tokens the new filter language understands are translated
 * (`status:pending` → `access:pending`, `kind:db-change` → `label:type::db-change`); any other
 * `key:value` word is dropped — the new parser would otherwise search for it as free text.
 * Plain words stay as text. Existing /requests views win on a name clash. Runs once (marker
 * key); every storage access is wrapped so a blocked/private storage never breaks the page.
 * Self-contained (no imports) so `scripts/check-saved-views-migration.mjs` runs it in plain node;
 * the output is already in `normalizeRequestQuery` form (tokens sorted, then text).
 */
export const REQUEST_VIEWS_KEY = 'infras.requests.v2.views';
export const LEGACY_VIEW_KEYS = ['infras.access.views', 'infras.changes.views', 'infras.requests.views'] as const;
const MIGRATED_KEY = 'infras.requests.v2.views.migrated';
const MAX_VIEWS = 20;

const ACCESS = new Set(['pending', 'approved', 'denied', 'cancelled', 'any']);
const KEEP = new Set(['state', 'label', 'access', 'assignee', 'requester', 'workflow', 'awaiting', 'due']);
const CHANGE_KINDS = new Set(['secret-rotation', 'deploy-config', 'db-change']);

/** Old-language query → new-language query (unknown filters dropped). */
export function translateLegacyViewQuery(q: string): string {
  const tokens: [string, string][] = [];
  const text: string[] = [];
  const put = (k: string, v: string) => {
    const at = k === 'label' ? tokens.findIndex((t) => t[0] === k && t[1] === v) : tokens.findIndex((t) => t[0] === k);
    if (at >= 0) tokens.splice(at, 1);
    tokens.push([k, v]);
  };
  for (const w of q.match(/[a-z]+:"[^"]*"?|\S+/gi) ?? []) {
    const i = w.indexOf(':');
    if (i <= 0) {
      text.push(w);
      continue;
    }
    const k = w.slice(0, i).toLowerCase();
    const v = w.slice(i + 1).replace(/^"/, '').replace(/"$/, '').trim().toLowerCase();
    if (!v) continue;
    if (k === 'status' && ACCESS.has(v)) put('access', v);
    else if (k === 'status' && (v === 'open' || v === 'closed')) put('state', v);
    else if ((k === 'kind' || k === 'type') && CHANGE_KINDS.has(v)) put('label', `type::${v}`);
    else if (KEEP.has(k)) put(k, v);
    // anything else (system:, risk:, expires:, …) has no equivalent — dropped
  }
  tokens.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
  const quote = (v: string) => (/\s/.test(v) ? `"${v}"` : v);
  return [...tokens.map(([k, v]) => `${k}:${quote(v)}`), text.join(' ')].filter(Boolean).join(' ');
}

function readViews(storage: Storage, key: string): { name: string; q: string }[] {
  try {
    const raw = JSON.parse(storage.getItem(key) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((x): x is { name: string; q: string } => !!x && typeof x.name === 'string' && typeof x.q === 'string')
      .map((x) => ({ name: x.name.trim(), q: x.q }))
      .filter((x) => x.name);
  } catch {
    return [];
  }
}

/** Returns how many views were migrated (0 when already done or nothing to move). */
export function migrateLegacyRequestViews(storage?: Storage | null): number {
  try {
    const s = storage ?? window.localStorage;
    if (s.getItem(MIGRATED_KEY)) return 0;
    const current = readViews(s, REQUEST_VIEWS_KEY);
    const names = new Set(current.map((v) => v.name));
    const added: { name: string; q: string }[] = [];
    for (const key of LEGACY_VIEW_KEYS) {
      for (const v of readViews(s, key)) {
        if (names.has(v.name)) continue;
        const q = translateLegacyViewQuery(v.q);
        if (!q) continue; // nothing the new list understands
        names.add(v.name);
        added.push({ name: v.name, q });
      }
    }
    if (added.length) s.setItem(REQUEST_VIEWS_KEY, JSON.stringify([...current, ...added].slice(-MAX_VIEWS)));
    s.setItem(MIGRATED_KEY, new Date().toISOString());
    return added.length;
  } catch {
    return 0; // storage unavailable — try again next load
  }
}
